<template>
  <v-navigation-drawer :model-value="true" permanent :rail="rail" :width="230" rail-width="56" class="standalone-nav">
    <div class="standalone-nav__brand" :class="{ 'standalone-nav__brand--rail': rail }">
      <LogoTile :src="logoUrl" :alt="tenantName" :size="28" />
      <span v-if="!rail" class="standalone-nav__name">{{ tenantName }}</span>
    </div>

    <v-list nav density="compact" class="standalone-nav__list">
      <template v-for="item in navItems" :key="item.route">
        <v-list-item
          :active="isActive(item.route)"
          :prepend-icon="materialIcon(item.icon)"
          :title="rail ? undefined : item.name"
          @click="onItemClick(item)"
        />
        <template v-if="!rail && hasItems(item) && expanded === item.route">
          <v-list-item
            v-for="sub in item.items"
            :key="sub.route"
            :active="isActive(sub.route)"
            :prepend-icon="materialIcon(sub.icon) || 'chevron_right'"
            :title="sub.name"
            class="standalone-nav__sub"
            @click="onSubClick(sub)"
          />
        </template>
      </template>
    </v-list>

    <template #append>
      <v-list nav density="compact">
        <v-list-item
          :prepend-icon="rail ? 'chevron_right' : 'chevron_left'"
          :title="rail ? undefined : 'Collapse'"
          @click="$emit('update:rail', !rail)"
        />
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { navigateToUrl } from "single-spa";
import { useSessionStore } from "@/stores/session";
import LogoTile from "./LogoTile.vue";
import type { NavBarItem, NavBarSubItem } from "@/tenant/types";

export default defineComponent({
  name: "Sidebar",
  components: { LogoTile },
  props: {
    rail: { type: Boolean, default: false },
  },
  emits: ["update:rail"],
  setup() {
    const session = useSessionStore();
    const expanded = ref<string | null>(null);
    const path = ref(window.location.pathname + window.location.search);

    window.addEventListener("single-spa:before-routing-event", () => {
      path.value = window.location.pathname + window.location.search;
    });
    window.addEventListener("popstate", () => {
      path.value = window.location.pathname + window.location.search;
    });

    const navItems = computed(() => session.navItems);
    const tenantName = computed(() => session.tenant?.name || "MetaShip");
    const logoUrl = computed(() => {
      const branding = session.tenant?.branding;
      const dark = branding?.defaultTheme !== "light";
      return (dark ? branding?.logoDarkUrl : branding?.logoLightUrl) || branding?.logoLightUrl || null;
    });

    function hasItems(item: NavBarItem): boolean {
      return !!(item.items && item.items.length);
    }

    /**
     * A route WITH a `?section=` (a specific sub-item, or a module collapsed
     * to a single allowed item) is active only on an EXACT path+section
     * match — otherwise every sub-item under the same bare path lit up
     * together the instant the user was anywhere in the module. A route
     * with NO section (a parent/default row) keeps matching its own path
     * plus any nested sub-path.
     */
    function isActive(route: string): boolean {
      const [routePath, routeQuery] = route.split("?");
      const routeSection = new URLSearchParams(routeQuery || "").get("section");
      const [currentPath, currentQuery] = path.value.split("?");
      const currentSection = new URLSearchParams(currentQuery || "").get("section");

      if (routeSection != null) {
        return currentPath === routePath && currentSection === routeSection;
      }
      return currentPath === routePath || (currentPath ?? "").startsWith(`${routePath}/`);
    }

    function onItemClick(item: NavBarItem): void {
      if (hasItems(item)) {
        expanded.value = expanded.value === item.route ? null : item.route;
      }
      navigateToUrl(item.route);
    }

    function onSubClick(sub: NavBarSubItem): void {
      const samePath = path.value.split("?")[0] === sub.route.split("?")[0];
      navigateToUrl(sub.route);
      if (samePath) {
        const [p, q] = sub.route.split("?");
        window.dispatchEvent(
          new CustomEvent("metaship:navigate", { detail: { path: p, query: Object.fromEntries(new URLSearchParams(q || "")) } }),
        );
      }
    }

    // Material icon ligature names come straight off the NavBarList payload
    // (metaship-backend Static/Navbar.ts) — no translation needed.
    function materialIcon(icon?: string): string | undefined {
      return icon;
    }

    return { navItems, tenantName, logoUrl, expanded, hasItems, isActive, onItemClick, onSubClick, materialIcon };
  },
});
</script>

<style scoped>
.standalone-nav__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
}
.standalone-nav__brand--rail {
  justify-content: center;
  padding: 12px 0;
}
.standalone-nav__name {
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-nav__sub :deep(.v-list-item-title) {
  font-size: 12px;
}

/* Sit BELOW the fixed TopBar rather than under it — Vuetify's own app-bar/
   navigation-drawer layout registration handles this automatically inside
   THIS <v-app>, but forced explicitly (!important) so the drawer's position
   is deterministic and matches the SAME --metaship-header-height contract
   the single-spa modules read for their own padding-top, rather than
   depending on two different mechanisms staying in sync by accident. */
.standalone-nav {
  top: var(--metaship-header-height, 0px) !important;
  height: calc(100% - var(--metaship-header-height, 0px)) !important;
}
</style>
